// Main wizard container that manages the overall wizard layout and state

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useWizardState } from '../hooks/useWizardState';
import { WizardStep, StoryType, stepAnimationVariants } from '../types';
import { wizardLogger } from '../utils/logger';

interface WizardContainerProps {
  children: React.ReactNode;
  currentStep: WizardStep;
  onStepChange?: (step: WizardStep) => void;
  className?: string;
  storyType: StoryType | null;
}

export function WizardContainer({ 
  children, 
  currentStep,
  onStepChange,
  className,
  storyType
}: WizardContainerProps) {
  // Minimal logging for performance
  const [direction, setDirection] = React.useState(0);
  const [previousStep, setPreviousStep] = React.useState<WizardStep>(currentStep);

  // Track step changes for animation direction
  React.useEffect(() => {
    if (currentStep !== previousStep) {
      const steps = storyType === 'multi' ? 
        ['story-type', 'photo-upload', 'multi-character-details', 'multi-character-appearance', 'style', 'story-creation', 'story-preview'] :
        ['story-type', 'photo-upload', 'character-details', 'appearance', 'style', 'story-creation', 'story-preview'];
      
      const currentIndex = steps.indexOf(currentStep);
      const previousIndex = steps.indexOf(previousStep);
      
      setDirection(currentIndex > previousIndex ? 1 : -1);
      setPreviousStep(currentStep);
      
      wizardLogger.stepTransition(previousStep, currentStep, currentIndex > previousIndex ? 'next' : 'back');
    }
  }, [currentStep, previousStep, storyType]);

  return (
    <div className={cn(
      "min-h-screen bg-gradient-to-b from-purple-50 to-pink-50",
      "py-0 px-0 sm:py-12 sm:px-4 lg:px-8", // No padding on mobile
      className
    )}>
      <div className="max-w-3xl mx-auto h-full sm:h-auto" data-wizard-container="true">
        <Card className={cn(
          "shadow-lg",
          "h-full sm:h-auto", // Use h-full instead of min-h-screen
          "rounded-none sm:rounded-lg", // No border radius on mobile
          "flex flex-col" // Flex container for mobile layout
        )}>
          {children}
        </Card>
      </div>
    </div>
  );
}
